import React from 'react'
import PropTypes from 'prop-types'
import { FormTextField, useZionFormContext, useFormState } from '@fs/zion-form'
import { Row, Cell, LabelSpacer } from '@fs/zion-ui'
import { useSlsStateContext, useSlsHighlightsContext } from '@fs/zion-sls'
import { useTranslation } from 'react-i18next'
import HighlightButton from '../../panels/shared/HighlightButton'
import { prepareFieldForHighlights, handleInputBlur, handleInputFocus } from '../../shared/utils/highlightUtils'
import { validateLinkedEventFieldChange, getOriginalFormValue } from '../utils/events.utils'

/**
 * OtherEventFields
 *
 * Renders a single "other" event field with a text input and a highlight button.
 * The component wires into Zion form and highlights contexts to synchronize
 * form values and highlight state.
 *
 * Props
 * @param {Object} props
 * @param {Object} props.otherField - The metadata for the other field to render. Must include `displayLabel`.
 * @param {boolean} [props.primaryEvent=false] - Whether this field belongs to the primary event or an additional event.
 * @param {number} props.eventIndex - Index of the event in the events array.
 * @param {number} props.itemIndex - Index of the item in the other[] array for the event.
 * @param {boolean} [props.isLinkedEvent=false] - Whether this event is linked to multiple persons.
 *
 * @returns {JSX.Element} The rendered other event field row.
 */
const OtherEventFields = ({
  otherField,
  primaryEvent = false,
  eventIndex,
  itemIndex,
  isLinkedEvent = false,
  splitScreenImageArk,
}) => {
  const { getValues, control } = useZionFormContext()
  const { slsTypes, images } = useSlsStateContext()
  const h = useSlsHighlightsContext()
  const [t] = useTranslation()
  const { defaultValues } = useFormState({ control })
  const basePath = `events.${primaryEvent ? 'primary' : 'additional'}[${eventIndex}].other[${itemIndex}]`

  return (
    <Row gutters="xxs">
      <FormTextField
        name={`${basePath}.value`}
        label={otherField.displayLabel}
        data-testid={`other-event-field-${itemIndex}`}
        warningValidate={(val) => {
          const originalValue = getOriginalFormValue(defaultValues, primaryEvent, eventIndex, 'other', itemIndex)
          return validateLinkedEventFieldChange(isLinkedEvent, val, originalValue, t)
        }}
        onFocus={() => handleInputFocus(`${basePath}.id`, getValues, h)}
        onBlur={() => handleInputBlur(h)}
        growX
        dense
      />
      <Cell>
        <LabelSpacer />
        <HighlightButton
          field={prepareFieldForHighlights(getValues, otherField.type, basePath, slsTypes, images)}
          splitScreenImageArk={splitScreenImageArk}
          buttonProps={{ 'data-testid': `other-event-field-${itemIndex}-highlight-button` }}
          linkName={`essential-information-other-event-field-${itemIndex}-highlight`}
          growX
          iconOnly
        />
      </Cell>
    </Row>
  )
}
export default OtherEventFields

OtherEventFields.propTypes = {
  // Field metadata used to label the input. displayLabel is required.
  otherField: PropTypes.shape({
    displayLabel: PropTypes.string.isRequired,
    fieldType: PropTypes.string,
  }).isRequired,
  // If true, the field is part of the primary event, otherwise an additional event.
  primaryEvent: PropTypes.bool,
  // Index of the event in the events array
  eventIndex: PropTypes.number.isRequired,
  // Index of this item in the event's other[] array
  itemIndex: PropTypes.number.isRequired,
  // Whether this event is linked to multiple persons
  isLinkedEvent: PropTypes.bool,
}

OtherEventFields.defaultProps = {
  primaryEvent: false,
  isLinkedEvent: false,
}
